import { defineComponent as _defineComponent } from 'vue'
import { ref, watch } from 'vue'
import type {
  WheelsPickableAddress,
  WheelsSearchResult,
  WheelsSearchStrategy,
  WheelsPlaceDetailStrategy,
} from './WheelsAddressPicker.constants'
import WheelsSvgIcon from '../WheelsSvgIcon/WheelsSvgIcon.vue'
import WheelsTripMapPoint from '../WheelsTripMap/WheelsTripMapPoint.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'WheelsAddressPicker',
  props: {
    address: null,
    searchStrategy: null,
    placeDetailStrategy: null
  },
  emits: ["change"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'change', address: WheelsPickableAddress): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  address?: WheelsPickableAddress
  searchStrategy: WheelsSearchStrategy
  placeDetailStrategy: WheelsPlaceDetailStrategy
};





const loading = ref(false)
const detailLoading = ref(false)
const isDropdownActive = ref(false)
const search = ref('')
const debounce = ref<number | null>(null)
const recommendedAddresses = ref<Array<WheelsSearchResult>>([])
const arrowPosition = ref<number | undefined>(undefined)
const isSelectingAddress = ref(false)

// Match the initial search behavior
watch(
  () => props.address,
  (val) => {
    search.value = val?.name || ''
  },
  { immediate: true },
)

// Watch search value changes to trigger search
watch(
  () => search.value,
  (val) => {
    if (val === props.address?.name || isSelectingAddress.value) {
      return
    }
    addressAutoComplete(val)
  },
)

const addressAutoComplete = (input: string): void => {
  if (!input) {
    clearPlace()
    return
  }
  if (debounce.value) {
    window.clearTimeout(debounce.value)
  }

  debounce.value = window.setTimeout(async () => {
    loading.value = true
    arrowPosition.value = undefined
    try {
      recommendedAddresses.value = await props.searchStrategy(input)
      isDropdownActive.value = true
    } catch (error) {
      console.error('Error fetching autocomplete results:', error)
      recommendedAddresses.value = []
    } finally {
      loading.value = false
    }
  }, 250)
}

const handleKeyEvent = (event: KeyboardEvent): void => {
  const maxPosition = recommendedAddresses.value.length

  if (event.key === 'ArrowUp') {
    if (typeof arrowPosition.value === 'undefined') {
      arrowPosition.value = maxPosition
    } else if (arrowPosition.value >= 0) {
      arrowPosition.value = arrowPosition.value - 1
    }
    return
  }

  if (event.key === 'ArrowDown') {
    if (typeof arrowPosition.value === 'undefined') {
      arrowPosition.value = 0
    } else if (arrowPosition.value + 1 === maxPosition) {
      arrowPosition.value = 0
    } else {
      arrowPosition.value = arrowPosition.value + 1
    }
    return
  }

  if (event.key === 'Escape') {
    isDropdownActive.value = false
    arrowPosition.value = undefined
    return
  }

  if (event.key === 'Enter') {
    event.preventDefault()
    event.stopPropagation()
    if (
      typeof recommendedAddresses.value[arrowPosition.value as number] !==
      'undefined'
    ) {
      handleRecommendedAddressClick(
        recommendedAddresses.value[arrowPosition.value as number],
      )
    }
    return
  }
}

const handleRecommendedAddressClick = async (
  searchResult: WheelsSearchResult,
): Promise<void> => {
  isSelectingAddress.value = true
  detailLoading.value = true

  try {
    // Call placeDetailStrategy to fetch full address details
    const fullAddress = await props.placeDetailStrategy(
      searchResult.id,
      searchResult.source,
    )

    // Update the input display with the search result name
    search.value = searchResult.description || ''

    emit('change', fullAddress)

    isDropdownActive.value = false
  } catch (error) {
    console.error('Error fetching address details:', error)
  } finally {
    detailLoading.value = false
    // Reset the flag after a short delay to allow the next user input
    setTimeout(() => {
      isSelectingAddress.value = false
    }, 100)
  }
}

const clearPlace = (): void => {
  recommendedAddresses.value = []
  search.value = '' // this triggers the watch
  emit('change', {})
}

const onFocus = (): void => {
  isDropdownActive.value = true
  if (search.value) {
    addressAutoComplete(search.value)
  }
}

const onBlur = (): void => {
  setTimeout(() => {
    isDropdownActive.value = false
    recommendedAddresses.value = []
  }, 500)
}

const handleInputClick = (): void => {
  if (recommendedAddresses.value.length > 0) {
    isDropdownActive.value = true
  }
}

return { __sfc: true,props, emit, loading, detailLoading, isDropdownActive, search, debounce, recommendedAddresses, arrowPosition, isSelectingAddress, addressAutoComplete, handleKeyEvent, handleRecommendedAddressClick, clearPlace, onFocus, onBlur, handleInputClick, WheelsSvgIcon, WheelsTripMapPoint }
}

})